/**
 * `/settings` — locale, profile, budget.
 *
 * Small on purpose. The rest of what a settings screen in this app will eventually hold is
 * the reader's own: font size, measure, typewriter on/off, reduced motion — all of it client-side,
 * persisted to `localStorage`, and none of it worth inventing a shape for before the a11y
 * work that decides which of them are *required* rather than preferences.
 *
 * What is here is the one setting that is server state: **the locale**. It writes `PATCH /me`
 * and the mutation invalidates the translations cache (`useUpdateLocale`), which is what makes
 * the chrome change language on the next frame. Worth stating in the UI, because it is not
 * guessable: **a story's language is fixed when it is created.** The DM's voice for a run in
 * flight does not change under it, and the prompt cache would not thank you if it did — and
 * since the i18n pass, the interface *inside* a story follows the story rather than this setting
 * (`i18n/I18nProvider.tsx`), so a run started in English stays English end to end.
 */
import { describeApiError } from '@/api/errorMessages';
import { useProfile, useStoryBudget, useUpdateLocale } from '@/api/queries';
import { useAuth } from '@/auth/context';
import { LOCALES } from '@/features/settings/locales';
import { ReadingControls } from '@/features/settings/ReadingSettings';
import { useT } from '@/i18n/context';
import { Button } from '@/ui/Button';
import { Page } from '@/ui/Page';
import { toastOk } from '@/ui/toast';

export function SettingsRoute() {
  const t = useT();
  const { signOut } = useAuth();
  const profile = useProfile();
  const budget = useStoryBudget();
  const updateLocale = useUpdateLocale();

  return (
    <Page title={t('ui.settings.title')} back={{ to: '/stories', label: t('ui.nav.your_stories') }}>
      {profile.isPending ? (
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {t('ui.common.loading')}
        </p>
      ) : profile.error ? (
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {describeApiError(t, profile.error)}
        </p>
      ) : (
        <>
          <section className="flex flex-col gap-2">
            <label
              htmlFor="locale"
              className="text-fg-muted font-mono"
              style={{ fontSize: 'var(--text-meta)' }}
            >
              {t('ui.settings.locale')}
            </label>
            <select
              id="locale"
              value={profile.data.locale}
              disabled={updateLocale.isPending}
              onChange={(event) => {
                updateLocale.mutate(event.target.value, {
                  onSuccess: () => {
                    toastOk(t('ui.settings.locale_saved'));
                  },
                });
              }}
              className="border-border focus:border-accent text-fg w-fit border bg-transparent px-3 py-2 font-mono transition-colors outline-none disabled:opacity-40"
              style={{ fontSize: 'var(--text-action)' }}
            >
              {LOCALES.map((locale) => (
                <option key={locale.code} value={locale.code} className="bg-panel">
                  {locale.label}
                </option>
              ))}
            </select>
            <p className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
              {t('ui.settings.locale_hint')}
            </p>
            {updateLocale.error ? (
              <p
                role="alert"
                className="text-[color:var(--color-ramp-danger)]"
                style={{ fontSize: 'var(--text-meta)' }}
              >
                {describeApiError(t, updateLocale.error)}
              </p>
            ) : null}
          </section>

          {/*
            The reading settings, in their second home. The first is a drawer over the story,
            where a line length can actually be judged; this is here because a player who has
            made the text unreadably small on a phone should be able to find the way back
            without a story open.
          */}
          <section className="border-rule flex flex-col gap-4 border-t pt-6">
            <h2 className="text-fg-muted font-mono" style={{ fontSize: 'var(--text-meta)' }}>
              {t('ui.reading.title')}
            </h2>
            <ReadingControls />
          </section>

          <section className="border-rule flex flex-col gap-1 border-t pt-6">
            <p className="text-fg font-mono" style={{ fontSize: 'var(--text-action)' }}>
              {profile.data.email}
            </p>
            {budget ? (
              <p className="text-fg-faint font-mono" style={{ fontSize: 'var(--text-meta)' }}>
                {t('ui.settings.slots_used', { used: budget.used, max: budget.max })}
              </p>
            ) : null}
          </section>

          <nav
            className="text-fg-faint mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 font-mono"
            style={{ fontSize: 'var(--text-meta)' }}
          >
            {/* No feedback link here any more: `App` mounts a global one, and two identical
                controls on one screen is worse than the gap this used to cover. */}
            <Button variant="bare" className="ml-auto px-0" onClick={signOut}>
              {t('ui.auth.sign_out')}
            </Button>
          </nav>
        </>
      )}
    </Page>
  );
}
